import React from 'react'
import { FaXmark as CloseIcon } from 'react-icons/fa6'
import type { ResolvedPad } from '@/lib/resolvePad'
import { usePlatform } from '@/hooks/usePlatform'
import { useMidiMappable } from '@/hooks/useMidiMappable'
import { useUIStore } from '@/stores/uiStore'
import { useRecordToPad } from '@/hooks/useRecordToPad'
import { useSequencerStore } from '@/stores/sequencerStore'
import { WaveformThumbnail } from '@/components/sequencer/WaveformThumbnail'

interface PadGridProps {
  /** The 16 pad slots after resolution (padDefs override > kit default > null). */
  resolvedPads: Array<ResolvedPad | null>
  /** Which pad index is currently being edited in the step grid. */
  focusedPad: number
  /**
   * Fires when any kit pad is tapped. Empty slots also call this —
   * the parent opens PadEditor focused on that slot so the user can
   * drop content into it. Occupied pads play their sound + focus.
   */
  onPadTrigger: (padIndex: number) => void
  /**
   * Fires after a ~500ms press-and-hold. Default handler opens the
   * pad in the Inspector for editing — matching the Werkshop's
   * "long-press to edit" gesture elsewhere. Optional; pads still
   * work with just the short-tap handler when this is omitted.
   */
  onPadLongPress?: (padIndex: number) => void
}

/**
 * 4×4 pad grid + trailing overflow section for custom MIDI pads.
 *
 * Each of the 16 grid slots resolves to one of three content kinds
 * (synth / sample / midi) or null (empty). Empty slots render a
 * `+ Add` button that, on click, opens PadEditor focused on that
 * slot — so the user can assign any content type without leaving
 * the canvas.
 *
 * Occupied pads show a small kind-cue (icon + color accent) so the
 * grid reads at a glance: you can tell which slots are synth vs.
 * sample vs. MIDI without opening settings.
 */
export const PadGrid: React.FC<PadGridProps> = ({
  resolvedPads,
  focusedPad,
  onPadTrigger,
  onPadLongPress,
}) => {
  const platform = usePlatform()
  // Always render the full 4×4 grid. An earlier Phase 7 iteration hid
  // the bottom row (pads 12-15) when empty on native iPad to save
  // vertical space, but the layout-shift when the user first touched
  // a pad 12+ was worse than the space saved, and the user expects
  // 16 permanent slots to match MPC / Koala conventions.
  const visiblePadCount = 16
  // Native pads use a shorter 4:3 aspect so the full 4-row grid
  // (when bottom row is occupied) fits in the editor's height
  // budget. Web keeps the square grid for mouse-precision feel.
  const padAspectClass = platform.isNative ? 'aspect-[4/3]' : 'aspect-square'
  const padContainerClass = platform.isNative
    ? 'grid grid-cols-4 gap-2 rounded-xl bg-surface-sunken border border-brd-subtle p-3'
    : 'grid grid-cols-4 gap-3 rounded-xl bg-surface-sunken border border-brd-subtle p-4'
  return (
    <div className="flex flex-col gap-3">
      <div className={padContainerClass}>
        {Array.from({ length: visiblePadCount }).map((_, i) => (
          <PadButton
            key={i}
            padIndex={i}
            pad={resolvedPads[i] ?? null}
            focused={focusedPad === i}
            aspectClass={padAspectClass}
            onTrigger={onPadTrigger}
            onLongPress={onPadLongPress}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Individual pad button — extracted so each call-site can register a
 * unique MIDI mapping slot (`pad.N.trigger`) via `useMidiMappable`.
 * The hook must run once per pad index; lifting it into the parent
 * loop would violate hook rules.
 */
const PadButton: React.FC<{
  padIndex: number
  pad: ResolvedPad | null
  focused: boolean
  aspectClass: string
  onTrigger: (padIndex: number) => void
  onLongPress?: (padIndex: number) => void
}> = ({ padIndex, pad, focused, aspectClass, onTrigger, onLongPress }) => {
  const empty = !pad
  const recordingPadIndex = useUIStore(s => s.recordingPadIndex)
  const recordArmNext = useUIStore(s => s.recordArmNext)
  const setRecordArmNext = useUIStore(s => s.setRecordArmNext)
  const padEditMode = useUIStore(s => s.padEditMode)
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const isRecording = recordingPadIndex === padIndex
  // Record-to-pad plumbing for THIS pad index. Used when the
  // global `recordArmNext` flag is on and the user clicks here —
  // we intercept the trigger, start recording instead. A second
  // trigger (on any pad, including this one) stops + assigns.
  const rec = useRecordToPad(padIndex)
  const padMap = useMidiMappable<HTMLButtonElement>({
    id: `pad.${padIndex}.trigger`,
    kind: 'trigger',
    label: pad ? `Pad ${padIndex + 1} — ${pad.label}` : `Pad ${padIndex + 1}`,
    trigger: () => onTrigger(padIndex),
  })

  // Long-press detection — we mark the press as "handled by
  // long-press" so the subsequent synthetic click doesn't also
  // fire the sound. The ref survives across pointer events for
  // the same gesture.
  const longPressTimerRef = React.useRef<number | null>(null)
  const longPressHandledRef = React.useRef(false)
  const clearLongPress = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }
  const handlePointerDown = () => {
    if (!onLongPress) return
    longPressHandledRef.current = false
    clearLongPress()
    longPressTimerRef.current = window.setTimeout(() => {
      longPressHandledRef.current = true
      onLongPress(padIndex)
    }, 500)
  }
  const handlePointerCancel = () => {
    clearLongPress()
  }
  React.useEffect(() => clearLongPress, [])

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (padMap.armForLearn(e)) return
    clearLongPress()
    // Swallow the synthetic click that follows a completed
    // long-press — otherwise the pad would both open the editor
    // AND fire the sound.
    if (longPressHandledRef.current) {
      longPressHandledRef.current = false
      return
    }
    // Tapping the pad that's currently recording stops + assigns
    // the take to this pad. Matches the sampler-hardware
    // convention (tap same button to stop).
    if (isRecording) {
      void rec.stop().catch(() => {
        /* surfaces via the hook error field */
      })
      return
    }
    // Arm intercept — when ARM is on and nothing is recording yet,
    // starting recording on THIS pad instead of auditioning. Clear
    // the arm flag as a single-shot so the user taps ARM each time
    // they want to record (mirrors Ableton's session-record feel).
    if (recordArmNext && recordingPadIndex === null) {
      setRecordArmNext(false)
      void rec.start().catch(() => {
        /* surfaces via the hook error field */
      })
      return
    }
    // If a DIFFERENT pad is recording, suppress the trigger (don't
    // audition), but don't try to stop it from here — the user
    // needs to tap that pad or press Escape (handled at the
    // ArmRecordButton level) to stop. Returning here avoids the
    // surprising "I tapped pad 3 but pad 5 played" situation.
    if (recordingPadIndex !== null) return
    onTrigger(padIndex)
  }

  const showDeleteButton = padEditMode && !empty && !isRecording
  const handleDeleteClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    e.preventDefault()
    // Cancel any in-flight long-press; we don't want the pad to
    // simultaneously clear AND open its editor.
    clearLongPress()
    longPressHandledRef.current = true
    setPadContent(padIndex, null)
  }

  return (
    <div className={`relative ${aspectClass}`}>
      <button
        type="button"
        ref={padMap.ref}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerCancel}
        onPointerLeave={handlePointerCancel}
        onPointerCancel={handlePointerCancel}
        aria-pressed={focused}
        aria-label={empty ? `Add content to pad ${padIndex + 1}` : pad.label}
        className={[
          'relative w-full h-full',
          'rounded-xl border p-3 text-center transition-all grid place-items-center',
          isRecording && rec.countingIn
            ? 'bg-gradient-to-br from-accent/60 to-accent/30 border-accent text-[color:var(--accent-ink)] shadow-[0_0_20px_var(--accent-halo),inset_0_1px_0_rgba(255,255,255,0.2)] animate-pulse'
            : isRecording
              ? 'bg-gradient-to-br from-destructive/90 to-destructive/60 border-destructive text-white shadow-[0_0_20px_rgba(239,68,68,0.6),inset_0_1px_0_rgba(255,255,255,0.2)] animate-pulse'
              : empty
                ? 'bg-surface-raised/40 border-dashed border-brd-subtle text-content-tertiary hover:border-brd-emphasis hover:text-content-secondary hover:bg-surface-raised/60'
                : focused
                  ? 'bg-gradient-to-br from-accent to-[color:var(--accent-dark)] border-accent text-[color:var(--accent-ink)] shadow-[0_0_20px_var(--accent-halo),inset_0_1px_0_rgba(255,255,255,0.2)]'
                  : 'bg-gradient-to-br from-surface-raised to-surface-base border-brd-subtle hover:border-[color:var(--accent-halo)] hover:-translate-y-0.5',
        ].join(' ')}
      >
        <span
          className={`absolute top-1.5 left-2 font-mono text-[10px] tracking-wider ${
            focused
              ? 'text-[color:var(--accent-ink)] opacity-60'
              : 'text-content-tertiary'
          }`}
        >
          {String(padIndex + 1).padStart(2, '0')}
        </span>
        {pad && (
          <span
            className={`absolute top-1.5 right-2 font-mono text-[9px] tracking-wider uppercase ${
              focused
                ? 'text-[color:var(--accent-ink)] opacity-70'
                : pad.kind === 'sample'
                  ? 'text-accent'
                  : pad.kind === 'granular'
                    ? 'text-accent font-bold'
                    : pad.kind === 'midi'
                      ? 'text-second'
                      : 'text-content-tertiary'
            }`}
            title={
              pad.kind === 'synth'
                ? 'Procedural voice'
                : pad.kind === 'sample'
                  ? 'Uploaded sample'
                  : pad.kind === 'granular'
                    ? 'Granular sample'
                    : 'External MIDI'
            }
          >
            {pad.kind === 'synth'
              ? 'syn'
              : pad.kind === 'sample'
                ? 'smp'
                : pad.kind === 'granular'
                  ? 'gra'
                  : 'mid'}
          </span>
        )}
        <span
          className={`text-xs font-semibold tracking-tight px-1 truncate max-w-full ${
            empty ? 'italic' : ''
          }`}
        >
          {pad ? pad.label : '+ Add'}
        </span>
        {/* Waveform silhouette for sample / granular pads — sits at
          the bottom edge of the tile at reduced opacity so the
          label stays readable. Hidden while recording (the pulse
          ring is the full story in that state). */}
        {pad?.sampleId && !isRecording && (
          <div
            className="absolute inset-x-1.5 bottom-1.5 opacity-40 pointer-events-none"
            aria-hidden
          >
            <WaveformThumbnail
              sampleId={pad.sampleId}
              width={80}
              height={18}
              color={focused ? 'currentColor' : 'var(--accent)'}
            />
          </div>
        )}
      </button>
      {showDeleteButton && (
        <span
          role="button"
          tabIndex={0}
          onClick={handleDeleteClick}
          onPointerDown={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleDeleteClick(
                e as unknown as React.MouseEvent<HTMLSpanElement>,
              )
            }
          }}
          title={`Clear pad ${padIndex + 1}`}
          aria-label={`Clear pad ${padIndex + 1}`}
          className="absolute top-1 right-1 grid place-items-center w-5 h-5 rounded-full bg-destructive/90 hover:bg-destructive text-white cursor-pointer shadow-medium z-10 transition-colors"
        >
          <CloseIcon size={10} />
        </span>
      )}
    </div>
  )
}
